/**
 * Single React Flow subscription for selection chrome used by node toolbars.
 * Nodes read scalars via context instead of each running O(N) filter selectors.
 *
 * - selectedNodeCount — all selected nodes
 * - isMultiSelectToolbarOpen — same predicate as CanvasPage
 *   (`multiSelectNodesSelector` length >= 2)
 */

import { useStore } from "@xyflow/react";
import { createContext, useContext, useMemo, type ReactNode } from "react";
import {
  multiSelectNodesSelector,
  selectedNodeCountSelector,
  shallowEqualArray,
} from "../utils/reactFlowSelectors";

type CanvasSelectionChrome = {
  selectedNodeCount: number;
  isMultiSelectToolbarOpen: boolean;
};

const CanvasSelectionCountContext = createContext<CanvasSelectionChrome | null>(
  null,
);

export function CanvasSelectionCountProvider({
  children,
}: {
  children: ReactNode;
}) {
  const selectedNodeCount = useStore(selectedNodeCountSelector);
  const multiSelectNodes = useStore(
    multiSelectNodesSelector,
    shallowEqualArray,
  );
  const isMultiSelectToolbarOpen = multiSelectNodes.length >= 2;

  const value = useMemo(
    () => ({ selectedNodeCount, isMultiSelectToolbarOpen }),
    [selectedNodeCount, isMultiSelectToolbarOpen],
  );

  return (
    <CanvasSelectionCountContext.Provider value={value}>
      {children}
    </CanvasSelectionCountContext.Provider>
  );
}

function useCanvasSelectionChrome(): CanvasSelectionChrome {
  const value = useContext(CanvasSelectionCountContext);
  if (value === null) {
    throw new Error(
      "useCanvasSelectionCount must be used within CanvasSelectionCountProvider",
    );
  }
  return value;
}

export function useCanvasSelectionCount(): number {
  return useCanvasSelectionChrome().selectedNodeCount;
}

/** True when CanvasPage would mount MultiSelectFloatingToolbar. */
export function useIsMultiSelectToolbarOpen(): boolean {
  return useCanvasSelectionChrome().isMultiSelectToolbarOpen;
}
